// The notification centre's state: the agent's latest notifications and the
// unread count. Loaded from the server on mount and after a reconnect, and
// kept live by `agent:notification` events (lib/bus-bridge.ts).

import { create } from 'zustand';
import type { AgentNotification } from '@prakash-software/shared';
import { fetchNotifications, markAllNotificationsRead, markNotificationRead } from '../lib/notifications-api';

/** The server returns at most this many; keep the live list to the same. */
const KEEP = 50;

interface State {
  items: AgentNotification[];
  unread: number;
  load: () => Promise<void>;
  add: (n: AgentNotification) => void;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export const useAgentNotifications = create<State>((set, get) => ({
  items: [],
  unread: 0,
  load: async () => {
    try {
      const { notifications, unread } = await fetchNotifications();
      set({ items: notifications, unread });
    } catch {
      /* keep what we have; the next load retries */
    }
  },
  add: (n) =>
    set((s) =>
      s.items.some((i) => i.id === n.id)
        ? s
        : { items: [n, ...s.items].slice(0, KEEP), unread: s.unread + (n.readAt ? 0 : 1) },
    ),
  markRead: async (id) => {
    const item = get().items.find((i) => i.id === id);
    if (!item || item.readAt) return;
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, readAt: Date.now() } : i)),
      unread: Math.max(0, s.unread - 1),
    }));
    await markNotificationRead(id).catch(() => {});
  },
  markAllRead: async () => {
    const now = Date.now();
    set((s) => ({ items: s.items.map((i) => (i.readAt ? i : { ...i, readAt: now })), unread: 0 }));
    await markAllNotificationsRead().catch(() => {});
  },
}));
